import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SupportList.css";
import Button from "../../../components/Button/Button";
import Table from "../../../components/Table/Table";
import Filters from "../../../components/Filters/Filters";
import eyeOn from "../../../assets/tokens/eyeOn.png";
import userSupport from "../../../assets/user_support.png";
//services
import { supportService } from "../../../services/Support";
//utils
import { formatDate } from "../../../utils/utils";
import Badge from "../../../components/Badge/Badge";
import Tooltip from "../../../components/Tooltip/Tooltip";

const SupportList = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [supportData, setSupportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  // Efecto para el término de búsqueda con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        fetchSupportData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Efecto para cargar datos iniciales y cuando cambian los filtros
  useEffect(() => {
    fetchColumns();
    fetchSupportData();
  }, [currentPage, selectedStatus, selectedDate]);

  const handleFilterChange = (filters) => {
    console.log("Applying filters:", filters);
    if (filters.status) {
      const statusMap = {
        Pending: "pending",
        Process: "in_progress",
        Complete: "completed",
      };
      const mapped = statusMap[filters.status] || filters.status;
      setSelectedStatus(mapped);
    } else {
      setSelectedStatus("");
    }
    if (filters.dateOfRequest) {
      setSelectedDate(filters.dateOfRequest);
    } else {
      setSelectedDate("");
    }
  };

  const fetchColumns = () => {
    const columnsData = [
      { header: "Support Folio", field: "folio" },
      { header: "Who requested it?", field: "requestedBy" },
      { header: "E-mail", field: "email" },
      { header: "Group name", field: "group" },
      { header: "Reason", field: "reason" },
      { header: "Date of request", field: "date" },
      {
        header: "Status",
        render: (row) => <Badge status={row.status} />,
      },
      {
        header: "",
        render: (row) => (
          <Tooltip text="View details of this ticket">
            <button className="view-btn" onClick={() => handleView(row)}>
              <img src={eyeOn} alt="View" />
            </button>
          </Tooltip>
        ),
      },
      {
        header: "",
        render: (row) => (
          <Tooltip text="Assign staff to this ticket">
            <button className="view-btn" onClick={() => handleAssign(row)}>
              <img src={userSupport} alt="View" />
            </button>
          </Tooltip>
        ),
      },
      {
        header: "",
        render: (row) => (
          <Tooltip text="Check">
            <button className="check-btn" onClick={() => handleCheck(row)}>
              Check
            </button>
          </Tooltip>
        ),
      },
    ];

    setColumns(columnsData);
  };

  const fetchSupportData = async () => {
    try {
      const params = {
        page: currentPage,
        items_per_page: 10,
        ...(searchTerm && { query: searchTerm }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedDate && { date: selectedDate }),
      };

      const response = await supportService.getList(params);
      if (response.status === "success") {
        const formattedData = response.data.data.map((support) => ({
          ...support,
          folio: support.folio_number || support.id,
          requestedBy: support.user?.name || "-",
          email: support.user?.email || "-",
          contact_number: support.contact_number || "-",
          reason: support.reason,
          date: formatDate(support.requested_at),
          status: support.status,
          group:
            !support.group ||
            !support.group.name ||
            support.group.name.trim() === ""
              ? "N/A"
              : support.group.name,
        }));
        setSupportData(formattedData);
        setTotalItems(response.data.total);
      }
    } catch (error) {
      console.error("Error fetching support data:", error);
    }
  };

  const handleView = (row) => {
    console.log("View support:", row);
    // Implement view logic
    navigate(`/support/details/${row.id}`, {
      state: {
        support: row,
        action: "view",
      },
    });
  };

  const handleAssign = (row) => {
    console.log("Assign support:", row);
    // Implement assign logic
    navigate(`/support/assign/${row.id}`, {
      state: {
        support: row,
        action: "assign",
      },
    });
  };

  const handleCheck = (row) => {
    console.log("Check support:", row);
    // Implement check logic
    navigate(`/support/check/${row.id}`, {
      state: {
        support: row,
        action: "check",
      },
    });
  };

  const handleDownload = () => {
    const toISO = (d) => {
      if (!d) return undefined;
      const [dd, mm, yyyy] = d.split("/");
      if (!dd || !mm || !yyyy) return undefined;
      return `${yyyy}-${mm}-${dd}`;
    };

    const getRange = () => {
      if (!selectedDate) return {};
      if (selectedDate.includes(" - ")) {
        const [startStr, endStr] = selectedDate
          .split(" - ")
          .map((s) => s.trim());
        const start = toISO(startStr);
        const end = toISO(endStr);
        return {
          ...(start && { start_date: start }),
          ...(end && { end_date: end }),
        };
      }
      const start = toISO(selectedDate.trim());
      return { ...(start && { start_date: start }) };
    };

    const params = {
      page: 1,
      items_per_page: totalItems > 0 ? totalItems : 999999,
      ...(searchTerm && { query: searchTerm }),
      ...(selectedStatus && { status: selectedStatus }),
      ...getRange(),
    };

    supportService.downloadSupport(params);
  };

  return (
    <div className="groups-management-page">
      <div className="groups-management-container">
        <div className="actions-bar">
          <div className="content-filter">
            <Filters
              filters={[
                {
                  id: "status",
                  label: "Status",
                  type: "checkbox",
                  options: ["pending", "process", "completed"],
                },
                {
                  id: "dateOfRequest",
                  label: "Date of request",
                  type: "date",
                  placeholder: "dd/mm/yyyy - dd/mm/yyyy",
                },
              ]}
              onApplyFilters={handleFilterChange}
            />
          </div>

          <div className="action-buttons">
            <Button
              variant="secondary"
              children="Suggested price"
              onClick={() => navigate(`/support/suggested-price`)}
              borderRadius={10}
            />
            <Button
              variant="warning"
              children="Download"
              onClick={handleDownload}
              borderRadius={10}
            />
          </div>
        </div>

        <div className="table-container">
          <Table
            columns={columns}
            data={supportData}
            columnsShare={3}
            currentPage={currentPage}
            itemsPerPage={10}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onSearch={(term) => setSearchTerm(term)}
            searchPlaceholder="Search by folio, name or email..."
          />
        </div>
      </div>
    </div>
  );
};

export default SupportList;
